<script setup lang="ts">
import { CircleStencil, Cropper, RectangleStencil } from 'vue-advanced-cropper';
import 'vue-advanced-cropper/dist/style.css';
import { computed, onMounted, reactive, ref, shallowRef } from 'vue';
import type { SavingOptions } from '@/services/types/file-uploader.types';

export type ImageType = 'avatar' | 'community' | 'card' | 'horizontal';

type StencilProps = {
  aspectRatio?: number;
  minAspectRatio?: number;
  maxAspectRatio?: number;
  handlers?: object;
  lines?: object;
  movable?: boolean;
  resizable?: boolean;
};

// PROPS
const props = defineProps<{
  type: ImageType;
  aspectRatio?: number;
  onSaveImage: (options: SavingOptions) => Promise<void>;
}>();

// EMITS
const emit = defineEmits<{
  // (e: "save", image: string): void,
  // (e: "remove"): void,
  (e: 'cancel'): void;
}>();

// REACTIVE
const imageSource = reactive({
  src: '',
  type: 'image/jpg',
});

// REFS
const cropRef = ref();
const file = ref();
const bannerRatio = ref(6.68);
const setImage = ref(false);
const editMode = ref<boolean>(false);
const isSaving = ref<boolean>(false);
const selectedStencil = shallowRef(CircleStencil);
const stencilProps = ref<StencilProps>({
  handlers: {},
  lines: {},
  movable: false,
  resizable: false,
});

// LIFECYCLE
onMounted(() => {
  selectStencil();
});

// COMPUTED
const hasImageSet = computed(() => setImage.value);

// FUNCTIONS
function toggleEditMode() {
  editMode.value = !editMode.value;
  if (editMode.value) {
    stencilProps.value.handlers = {
      eastNorth: true,
      north: true,
      westNorth: true,
      west: true,
      westSouth: true,
      south: true,
      eastSouth: true,
      east: true,
    };
    stencilProps.value.lines = {
      north: true,
      west: true,
      south: true,
      east: true,
    };
    stencilProps.value.movable = true;
    stencilProps.value.resizable = true;
  } else {
    stencilProps.value.handlers = {};
    stencilProps.value.lines = {};
    stencilProps.value.movable = false;
    stencilProps.value.resizable = false;
  }
}

function selectStencil() {
  switch (props.type) {
    case 'card':
      selectedStencil.value = RectangleStencil;
      stencilProps.value.aspectRatio = 3.86;
      break;
    case 'community':
      selectedStencil.value = RectangleStencil;
      stencilProps.value.aspectRatio = props.aspectRatio ?? 5.33;
      break;
    case 'horizontal':
      selectedStencil.value = RectangleStencil;
      stencilProps.value.aspectRatio = 5;
      break;
    default:
      selectedStencil.value = CircleStencil;
      stencilProps.value.aspectRatio = 1;
      break;
  }
}

function openImageDialog() {
  file.value.click();
}

function uploadImage(event: { target: { files: any } }) {
  const { files } = event.target;
  if (files && files[0]) {
    if (imageSource.src) {
      URL.revokeObjectURL(imageSource.src);
    }
    imageSource.src = URL.createObjectURL(files[0]);
    imageSource.type = files[0].type;
    setImage.value = true;
    toggleEditMode();
  }
}

async function handleSave() {
  if (cropRef.value) {
    try {
      isSaving.value = true;
      const { canvas } = cropRef.value.getResult();
      // emit("save", canvas.toDataURL());
      await props.onSaveImage({ image: canvas.toDataURL(), version: 'v1' });
    } finally {
      isSaving.value = false;
    }
  }
}

function handleCancel() {
  emit('cancel');
}
</script>

<template>
  <div class="base-image-upload" v-if="hasImageSet">
    <cropper
      :key="bannerRatio"
      ref="cropRef"
      class="cropper"
      :src="imageSource.src"
      :image-restriction="'fit-area'"
      :stencil-component="selectedStencil"
      :stencil-props="stencilProps"
    ></cropper>
    <div class="base-image-upload__buttons">
      <div class="base-image-upload__buttons--edit">
        <div class="base-image-upload__buttons--left">
          <q-btn @click="handleCancel" flat label="Cancel" />
        </div>
        <div class="base-image-upload__buttons--right">
          <q-btn color="primary" @click="handleSave" label="Save" :loading="isSaving" />
        </div>
      </div>
    </div>
  </div>

  <div class="base-image-upload__new" v-else>
    <div class="base-image-upload__new--border" @click="openImageDialog()">
      <div class="base-image-upload__new--text">{{ $t('upload-image-modal.add-photo') }}</div>
    </div>
    <div class="base-image-upload__buttons">
      <div class="base-image-upload__buttons--edit">
        <div class="base-image-upload__buttons--left">
          <q-btn @click="handleCancel" flat :label="$t('global.cancel')" />
        </div>
      </div>
    </div>
    <input class="base-image-upload__new--file-upload" type="file" ref="file" accept="image/*" @change="uploadImage" />
  </div>
</template>

<style lang="scss">
.base-image-upload {
  width: 750px;
  display: flex;
  flex-direction: column;

  &__new {
    height: 500px;
    width: 750px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    &--border {
      border: 2px #333333 dashed;
      border-radius: 8px;
      width: 100%;
      height: 100%;
      transition: 200ms;
      cursor: pointer;

      &:hover {
        border-color: #0000ff;
      }
    }

    &--text {
      font-size: 14px;
      font-weight: bold;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #333333;
      margin-bottom: 20px;
      transition: 200ms;

      &:hover {
        color: #0000ff;
      }
    }

    &--button {
      margin-top: 8px;
      width: 100%;
      display: flex;
      justify-content: flex-end;
    }

    &--file-upload {
      display: none;
    }
  }

  &__buttons {
    padding-top: 16px;
    display: flex;
    justify-content: space-between;

    &--edit {
      width: 100%;
      display: flex;
      justify-content: space-between;
    }

    &--right {
      display: flex;
    }

    &--ratio {
      display: flex;
      align-items: center;
      font-weight: bold;
      margin-right: 20px;
    }

    &--modify {
      width: 100%;
      display: flex;
      justify-content: space-between;
    }

    &--left {
      .base-button {
        margin-right: 20px;
      }
    }
  }
}

.cropper {
  height: 400px;
  width: 800px;
}

.button-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 17px;
}

.button {
  color: white;
  font-size: 14px;
  padding: 10px 20px;
  background: #0000ff;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.5s;
  border: none;

  &:not(:last-of-type) {
    margin-right: 10px;
  }

  &:hover {
    background: #2f2f2f;
  }

  input {
    display: none;
  }
}
</style>
